import React, { useEffect, useState, useRef } from 'react';
import translator from '@/translator';
import { Socket } from "phoenix";
import CircleCheckmark from '@components/CircleCheckmark';
import QRCodeViewer from '@components/QRCodeViewer';
import axios from 'axios';
import Helper from '@/helper';

const t = translator('front_office.components.whats_app_auth_widget.');

const RESTART_OPTION_TIMEOUT = 45 * 1000;
const AUTH_MESSAGE_CHECK_INTERVAL = 5 * 60 * 1000;
const RECONNECT_DELAY = 2 * 1000;



const AuthStatusBox = ({ authMessage }) => {
  return (
    <div className="qr_code">
      <div className="qr_code-box">
        <div className="qr_code-success" style={{ opacity: 0.3, display: 'block' }}>
          <CircleCheckmark />
        </div>
      </div>
    </div>
  );
};

const WhatsAppAuthWidget = ({
  customerId,
  apiKey,
  wsUrl,
  wsChannelName,
  subscriptionEnabled = true
}) => {
  const [authStatus, setAuthStatus] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inProgress, setInProgress] = useState(false);
  const [authMessage, setAuthMessage] = useState('');
  const [showRestartOption, setShowRestartOption] = useState(false);
  const [restarting, setRestarting] = useState(false);

  const socketRef = useRef(null);
  const channelRef = useRef(null);
  const restartTimeoutRef = useRef(null);
  const authTimeoutRef = useRef(null);
  const authCheckIntervalRef = useRef(null);

  const checkAuthMessage = async () => {
    try {
      const { data } = await axios.get('/front_office/whatsapp/should_show_auth_message');

      console.log('Auth message check:', data);

      if (data.is_authenticated) {
        if (data.should_show) {
          setAuthMessage(
            <div className="whatsapp-message">
              <p>{t('authorized_long.title')}</p>
              <p>{t('authorized_long.description')}</p>
            </div>
          );
        } else {
          setAuthMessage(t('authorized_short'));
        }
      }
    } catch (error) {
      console.error('Failed to check auth message:', error);
    }
  };

  useEffect(() => {
    if (authStatus) {
      axios.post('/front_office/whatsapp/auth_message', {}, {
        headers: {
          'X-CSRF-TOKEN': Helper.getCSRFToken(),
        }
      })
        .then(() => checkAuthMessage())
        .catch(error => console.error('Failed to set auth message:', error));

      authCheckIntervalRef.current = setInterval(checkAuthMessage, AUTH_MESSAGE_CHECK_INTERVAL);
    } else {
      if (authCheckIntervalRef.current) {
        clearInterval(authCheckIntervalRef.current);
        authCheckIntervalRef.current = null;
      }
      setAuthMessage('');
    }

    return () => {
      if (authCheckIntervalRef.current) {
        clearInterval(authCheckIntervalRef.current);
      }
    };
  }, [authStatus]);

  useEffect(() => {
    if (!authStatus && !restarting) {
      restartTimeoutRef.current = setTimeout(() => {
        setShowRestartOption(true);
      }, RESTART_OPTION_TIMEOUT);
    } else {
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
        restartTimeoutRef.current = null;
      }
      setShowRestartOption(false);
    }

    return () => {
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
      }
    };
  }, [authStatus, restarting]);

  const connectWebSocket = () => {
    if (socketRef.current) {
      return;
    }

    setLoading(true);
    setError('');
    setRestarting(false);
    setShowRestartOption(false);

    const socket = new Socket(wsUrl, {
      params: { token: apiKey },
      logger: (kind, msg, data) => {
        console.log(`${kind}: ${msg}`, data);
      },
    });

    socketRef.current = socket;
    socket.connect();

    const onError = (ev) => {
      console.log("ERROR", ev);
      setError(t('connection_error.title'));
      setLoading(false);
      setQrCode('');
    };

    socket.onOpen((ev) => console.log("OPEN", ev));
    socket.onError(onError);
    socket.onClose((e) => {
      console.log("CLOSE", e);
      if (!authStatus) {
        setError(t('reload_page'));
        setQrCode('');
      }
    });

    const channel = socket.channel(wsChannelName, {});
    channelRef.current = channel;

    const onAuthState = (msg) => {
      console.log("Auth state received:", msg);

      setAuthStatus(msg.auth);
      setInProgress(msg.in_progress || false);

      if (msg.auth === true) {
        setQrCode('');
        setLoading(false);
        setError('');
      } else if (msg.qr_code) {
        setQrCode(msg.qr_code);
        setLoading(false);
        setError('');
      } else {
        setLoading(true);
      }
    };

    channel
      .join()
      .receive("ok", (response) => {
        console.log("Joined successfully", response);
      })
      .receive("error", (response) => {
        console.log("Unable to join", response);
        setError(t('connection_error.title'));
        setLoading(false);
        setQrCode('');
      });

    channel.on("auth_state", onAuthState);

    channel.onError((e) => {
      console.log("Channel error", e);
      setError(t('connection_error.title'));
      setQrCode('');
    });

    channel.onClose((e) => {
      console.log("Channel closed", e);
      if (!authStatus) {
        setError(t('reload_page'));
        setQrCode('');
      }
    });
  };

  const restartAuth = () => {
    setRestarting(true);
    setShowRestartOption(false);

    if (channelRef.current) {
      channelRef.current.leave();
      channelRef.current = null;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setAuthStatus(false);
    setQrCode('');
    setError('');
    setInProgress(false);
    setLoading(true);
    setAuthMessage('');

    if (authCheckIntervalRef.current) {
      clearInterval(authCheckIntervalRef.current);
      authCheckIntervalRef.current = null;
    }

    axios.post('/front_office/whatsapp/restart_auth', {}, {
      headers: {
        'X-CSRF-TOKEN': Helper.getCSRFToken(),
      }
    })
      .then(() => {
        console.log('Restart API called successfully');
      })
      .catch(error => {
        console.error('Restart API failed:', error);
      });

    setTimeout(() => {
      connectWebSocket();
    }, RECONNECT_DELAY);
  };

  useEffect(() => {
    connectWebSocket();

    return () => {
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
      }
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
      }
      if (authCheckIntervalRef.current) {
        clearInterval(authCheckIntervalRef.current);
      }
      if (channelRef.current) {
        channelRef.current.leave();
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);



  const getStatusMessage = () => {
    if (restarting) {
      return (
        <div className="whatsapp-message">
          <p>{t('restart_success.title')}</p>
          <p>{t('restart_success.subtitle')}</p>
          <p>{t('restart_success.help_text')}</p>
        </div>
      );
    }

    if (authStatus) {
      return authMessage || t('authorized_short');
    }

    if (error) {
      if (error === t('reload_page')) {
        return error;
      }

      return (
        <div className="whatsapp-message">
          <p>{error}</p>
          <p>
            <a href="#" onClick={(e) => {
              e.preventDefault();
              restartAuth();
            }}>
              {t('connection_error.retry_text')}
            </a>
          </p>
        </div>
      );
    }

    if (showRestartOption && !qrCode) {
      return (
        <div className="whatsapp-message">
          <p>
            {t('qr_code_delayed.description')} {' '}
            <a href="#" onClick={(e) => {
              e.preventDefault();
              restartAuth();
            }}>
              {t('qr_code_delayed.link_text')}
            </a>
            {t('qr_code_delayed.action_text')}
          </p>
        </div>
      );
    }

    if (inProgress || loading) {
      return (
        <div className="whatsapp-message" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <p>{t('wait')}</p>
        </div>
      );
    }

    return t('authorize');
  };

  if (!subscriptionEnabled) {
    return null;
  }

  console.log('Render state:', {
    authStatus,
    qrCode: qrCode ? `Present (${qrCode.length} chars)` : 'Empty',
    loading,
    inProgress,
    error: error || 'none'
  });



  return (
    <section className="widget with_qr_code">
      <div className="header">
        <div>
          <div className="widget-title" style={{ textAlign: 'left' }}>{t('authorization_status')}</div>
          <div className="widget-sub_title">
            {getStatusMessage()}
          </div>
        </div>

        {authStatus ? (
          <AuthStatusBox authMessage={authMessage} />
        ) : (
          <div className="qr_code">
            <div className="qr_code-box">
              {(loading || inProgress || restarting) && (
                                 <lottie-player
                   src="https://assets2.lottiefiles.com/packages/lf20_dxd0khma.json"
                   background="transparent"
                   className="qr_code-loader"
                   style={{ opacity: 0.3, width: 55, height: 55, position: 'initial' }}
                   speed="1"
                   loop
                   autoplay
                 />
              )}
            </div>
          </div>
        )}
      </div>

      {!authStatus && qrCode && (
        <div className="widget-body">
          <QRCodeViewer
            value={qrCode}
            enlargeText={t('enlarge_code')}
            scanText={t('scan_code')}
        />
        </div>
      )}
    </section>
  );
};

export default WhatsAppAuthWidget;
